import { Suspense, lazy, useCallback, useEffect, useMemo, useState, type FC } from 'react';

import Dropzone from '@/components/common/Dropzone';
import useConvertFile from '@/hooks/useConvertFile';
import useTranslation from '@/hooks/useTranslation';
import { validateInputFile } from '@/utils/compression';
import { getServiceErrorTranslation } from '@/utils/errors';
import { lazyWithPreload } from '@/utils/miscellaneous';

const CompressionLevels = lazyWithPreload(() => import('@/components/compression/CompressionLevels'));
const Compressing = lazyWithPreload(() => import('@/components/compression/Compressing'));
const Download = lazyWithPreload(() => import('@/components/common/Download'));
const AdvertisingSection = lazy(() => import('@/components/compression/Footer'));
const Alert = lazy(() => import('@/components/common/Alert'));
const AdblockAlert = lazy(() => import('@/components/common/AdblockAlert'));

type ErrorCategoryType = 'info' | 'error';

interface PdfCompressorProps {
  onFinish?: () => void;
}

const PdfCompressor: FC<PdfCompressorProps> = ({ onFinish }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCategory, setErrorCategory] = useState<ErrorCategoryType>('info');
  const [showStrongCompression, setShowStrongCompression] = useState<boolean>(false);

  const { process, compressFile, conversionData, errorReason, resetErrorReason } = useConvertFile(onFinish);

  const { t } = useTranslation();

  // preloading lazy components
  useEffect(() => {
    CompressionLevels.preload().catch();
    Compressing.preload().catch();
    Download.preload().catch();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    try {
      const inputFile = acceptedFiles[0];
      validateInputFile(inputFile);
      setFile(inputFile);
    } catch (error: any) {
      setError(error.message);
    }
  }, []);

  const onReject = useCallback(() => {
    setError(t('pdfextra_convert_tool.errors.only_single_file_conversion'));
  }, [t]);

  const resetErrors = useCallback(() => {
    setError(null);
    setErrorCategory('info');
    resetErrorReason();
  }, [resetErrorReason]);

  // handling conversion service error
  useEffect(() => {
    if (process === 'failed' && errorReason) {
      const translation = getServiceErrorTranslation(errorReason);

      setError(t(translation));
      setErrorCategory('error');
      setFile(null);
    }
  }, [process, errorReason, t]);

  const currentComponent = useMemo(() => {
    if (file === null)
      return (
        <Dropzone
          onDrop={onDrop}
          onReject={onReject}
          multiple={false}
          heading="pdfextra_convert_tool.dropzone.compression.heading"
          paragraphTranslation="pdfextra_convert_tool.dropzone.compression_start_paragraph"
        />
      );

    if (file && process === 'pending')
      return (
        <CompressionLevels
          file={file}
          onCompress={compressFile}
          onStrongCompress={() => setShowStrongCompression(true)}
        />
      );

    if (process === 'uploading' || process === 'compressing') return <Compressing title={process} file={file} />;

    if (process === 'ready' && conversionData) return <Download file={file} conversionData={conversionData} />;
  }, [file, process, compressFile, conversionData, onDrop, onReject]);

  return (
    <Suspense>
      <div className="tw-widget-root">
        <div className="tw-relative tw-bg-white tw-py-5 tw-p-5 sm:tw-p-[32px] tw-border-3 tw-border-ui-light-grey tw-rounded-t-footer">
          {currentComponent}

          <Alert message={error as string} show={error !== null} category={errorCategory} onClose={resetErrors} />
          <Alert
            message={t('pdfextra_convert_tool.errors.not_supported_strong_compression')}
            show={showStrongCompression}
            category="info"
            onClose={() => setShowStrongCompression(false)}
          />
          <AdblockAlert />
        </div>

        <AdvertisingSection />
      </div>
    </Suspense>
  );
};

export default PdfCompressor;
